import { useCallback } from 'react';
import { useAuthContext } from '@/context/AuthContext';
import { fetchWithAuthRetry } from './withAuthRetry';
import {
  getChatbotApiBaseUrl,
  isRecord,
  readChatbotErrorMessage,
  sendChatbotRequest,
  type ChatbotRequestOptions,
} from './chatbotApiRequest';
import type {
  ChatbotConversation,
  ChatbotConversationDetailResponse,
  ChatbotConversationListResponse,
  ChatbotConversationRenameResponse,
  ChatbotHistoryMessage,
} from '@/models/chatbot';
import { isChatbotActiveFilters } from '../utils/chatbotActiveFilters';
import { HistoryError, classifyStatus, runWithRequestTimeout, toHistoryError } from './historyError';

function isConversation(value: unknown): value is ChatbotConversation {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    (!('active_filters' in value) || isChatbotActiveFilters(value.active_filters))
  );
}

function isHistoryMessage(value: unknown): value is ChatbotHistoryMessage {
  return (
    isRecord(value) &&
    (value.role === 'user' || value.role === 'ai' || value.role === 'assistant') &&
    typeof value.content === 'string'
  );
}

function isListResponse(value: unknown): value is ChatbotConversationListResponse {
  return (
    isRecord(value) &&
    Array.isArray(value.conversations) &&
    value.conversations.every(isConversation) &&
    (!('next_token' in value) || value.next_token === null || typeof value.next_token === 'string')
  );
}

function isDetailResponse(value: unknown): value is ChatbotConversationDetailResponse {
  return (
    isRecord(value) &&
    isConversation(value.conversation) &&
    Array.isArray(value.messages) &&
    value.messages.every(isHistoryMessage)
  );
}

function isRenameResponse(value: unknown): value is ChatbotConversationRenameResponse {
  return (
    isRecord(value) &&
    isRecord(value.conversation) &&
    typeof value.conversation.id === 'string' &&
    typeof value.conversation.title === 'string'
  );
}

function normalizeNextToken(token: string): string {
  try {
    const parsed: unknown = JSON.parse(token);
    if (!isRecord(parsed)) {
      return token;
    }

    const normalized = Object.fromEntries(
      Object.entries(parsed).map(([key, value]): [string, unknown] => {
        if (typeof value === 'string' && /^\d+$/.test(value) && Number.isSafeInteger(Number(value))) {
          return [key, Number(value)];
        }
        return [key, value];
      }),
    );

    return JSON.stringify(normalized);
  } catch {
    return token;
  }
}

interface UseChatbotHistoryApiReturn {
  listConversations: (params: { limit: number; nextToken?: string }) => Promise<ChatbotConversationListResponse>;
  getConversation: (conversationId: string) => Promise<ChatbotConversationDetailResponse>;
  renameConversation: (conversationId: string, title: string) => Promise<ChatbotConversationRenameResponse>;
  deleteConversation: (conversationId: string) => Promise<void>;
}

const useChatbotHistoryApi = (): UseChatbotHistoryApiReturn => {
  const { token, ensureValidSession } = useAuthContext();

  const request = useCallback(
    async (path: string, options: ChatbotRequestOptions): Promise<Response> => {
      const baseUrl = getChatbotApiBaseUrl();
      if (!baseUrl) {
        throw new HistoryError(0, 'Chatbot API URL is not configured', 'unknown');
      }

      const url = `${baseUrl}${path}`;

      let response: Response;
      try {
        response = await runWithRequestTimeout(signal =>
          fetchWithAuthRetry(
            authToken => sendChatbotRequest(url, options, authToken, signal),
            { token, ensureValidSession },
            (status, message) => new HistoryError(status, message, classifyStatus(status)),
          ),
        );
      } catch (error) {
        throw toHistoryError(error);
      }

      if (!response.ok) {
        const message = await readChatbotErrorMessage(response);
        throw new HistoryError(response.status, message, classifyStatus(response.status));
      }

      return response;
    },
    [ensureValidSession, token],
  );

  const listConversations = useCallback(
    async ({ limit, nextToken }: { limit: number; nextToken?: string }): Promise<ChatbotConversationListResponse> => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (nextToken) {
        params.set('next_token', normalizeNextToken(nextToken));
      }

      const query = params.toString().replace(/\+/g, '%20');

      const response = await request(`/chatbot/conversations?${query}`, { method: 'GET' });
      const data: unknown = await response.json();

      if (!isListResponse(data)) {
        throw new HistoryError(0, 'Invalid conversation list response', 'unknown');
      }

      return data;
    },
    [request],
  );

  const getConversation = useCallback(
    async (conversationId: string): Promise<ChatbotConversationDetailResponse> => {
      const response = await request(`/chatbot/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'GET',
      });
      const data: unknown = await response.json();

      if (!isDetailResponse(data)) {
        throw new HistoryError(0, 'Invalid conversation detail response', 'unknown');
      }

      return data;
    },
    [request],
  );

  const renameConversation = useCallback(
    async (conversationId: string, title: string): Promise<ChatbotConversationRenameResponse> => {
      const response = await request(`/chatbot/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'PATCH',
        body: { title },
      });
      const data: unknown = await response.json();

      if (!isRenameResponse(data)) {
        throw new HistoryError(0, 'Invalid conversation rename response', 'unknown');
      }

      return data;
    },
    [request],
  );

  const deleteConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      await request(`/chatbot/conversations/${encodeURIComponent(conversationId)}`, { method: 'DELETE' });
    },
    [request],
  );

  return { listConversations, getConversation, renameConversation, deleteConversation };
};

export default useChatbotHistoryApi;
